import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { routes } from 'utils/routing';

import { useCmsQuestDetailLazyQuery } from './cms-hooks/use-cms-quest-detail';
import { QuestProvider } from './loyalty-quest';
import { useActivateQuest } from './loyalty-quest/use-activate-quest';
import { canActivateQuest, isQuestActivated, isQuestCompleted } from './loyalty-quest/utils';
import { QuestCard } from './quest-card';
import { QuestDetailModal } from './quest-detail-modal';
import { QuestDrawerContent } from './quest-drawer-content';
import { QuestDetailError } from './quest-drawer-content/quest-detail-error';
import { QuestDrawerSkeleton } from './quest-drawer-content/quest-detail-skeleton';
import { QuestTerms } from './quest-drawer-content/quest-terms';
import type { IQuestCardWrapperProps } from './types';
import { useParseQuestCardData } from './use-parse-quest-card-data';
import { getProductRequirementCmsIds } from './utils';

export const QuestCardWrapper = ({
  cmsQuestCard,
  quest,
  cmsQuestCardUI,
  questId,
}: IQuestCardWrapperProps) => {
  const [showQuestTerms, setShowQuestTerms] = useState<boolean>(false);
  const [openQuestDetailDrawer, setOpenQuestDetailDrawer] = useState<boolean>(false);
  const [
    activateQuest,
    { loading: loadingActivateQuestMutation, error: activateQuestError },
  ] = useActivateQuest({
    questId: quest.id,
  });
  const questRequirementCmsIds = useMemo(() => getProductRequirementCmsIds(quest), [quest]);
  const [
    cmsQuestDetailQuery,
    {
      loadingCmsQuestDetail,
      cmsQuestDetail,
      cmsQuestDetailUI,
      refetch: refetchCmsQuestDetail,
      error: cmsQuestDetailError,
    },
  ] = useCmsQuestDetailLazyQuery({ cmsId: quest.cmsId, questRequirementCmsIds });
  const {
    image,
    imageDescription,
    title,
    description,
    buttonText,
    linkText,
    badgeText,
  } = useParseQuestCardData(quest, cmsQuestCard, cmsQuestCardUI, cmsQuestDetail);
  const { linkTo } = useNavigation();

  const questDetailError = activateQuestError || cmsQuestDetailError;
  const loadingQuestDetail = loadingCmsQuestDetail || loadingActivateQuestMutation;
  const currentQuestId = useRef(questId || '');

  const callToActionCard = useCallback(() => {
    setOpenQuestDetailDrawer(true);

    if (canActivateQuest(quest.status)) {
      activateQuest();
    }
    cmsQuestDetailQuery();
  }, [activateQuest, cmsQuestDetailQuery, quest.status]);

  const onPressLinkAndImageHandler = useCallback(() => {
    setOpenQuestDetailDrawer(true);

    cmsQuestDetailQuery();
  }, [cmsQuestDetailQuery]);

  const onPressLinkHandler = useCallback(() => {
    setOpenQuestDetailDrawer(true);

    setShowQuestTerms(true);
  }, []);

  useEffect(() => {
    if (!loadingQuestDetail) {
      // If questId is set and not active, activate it
      if (currentQuestId.current === quest.id && !isQuestActivated(quest.status)) {
        callToActionCard();
        currentQuestId.current = '';
      } else {
        // If questId is set and is invalid, redirect home
        if (currentQuestId.current !== '') {
          linkTo(routes.base);
        }
      }
    }
  }, [
    currentQuestId,
    quest.id,
    callToActionCard,
    loadingQuestDetail,
    linkTo,
    quest.status,
    onPressLinkAndImageHandler,
  ]);

  const onCloseDrawer = useCallback(() => {
    // restart state
    setOpenQuestDetailDrawer(false);
    setShowQuestTerms(false);
  }, []);

  const onRefetch = useCallback(() => {
    if (canActivateQuest(quest.status)) {
      activateQuest();
    }

    refetchCmsQuestDetail();
  }, [activateQuest, quest.status, refetchCmsQuestDetail]);

  useEffect(() => {
    if (!cmsQuestDetail && !loadingCmsQuestDetail) {
      refetchCmsQuestDetail();
    }
  }, [cmsQuestDetail, loadingCmsQuestDetail, refetchCmsQuestDetail]);

  return (
    <>
      {!loadingCmsQuestDetail && (
        <Box alignItems="center" marginBottom="$1.5">
          <QuestCard
            image={image}
            altText={imageDescription}
            title={title}
            description={description}
            howToRedeem={cmsQuestDetail?.howToRedeem?.localeRaw}
            callToAction={callToActionCard}
            callToActionText={buttonText}
            linkText={linkText}
            badgeText={badgeText}
            onPressImage={onPressLinkAndImageHandler}
            onPressLink={onPressLinkHandler}
            isQuestCompleted={isQuestCompleted(quest.status)}
          />
        </Box>
      )}

      {openQuestDetailDrawer && (
        <QuestDetailModal
          title={title}
          onDismiss={onCloseDrawer}
          hideTitle={showQuestTerms || !!questDetailError}
        >
          {showQuestTerms ? (
            <QuestTerms cmsQuestTerms={cmsQuestCard.moreInfo} />
          ) : (
            <>
              {questDetailError ? (
                <QuestDetailError refetch={onRefetch} />
              ) : (
                <QuestProvider quest={quest}>
                  {loadingQuestDetail && <QuestDrawerSkeleton />}
                  {!loadingQuestDetail && cmsQuestDetail && cmsQuestDetailUI && (
                    <QuestDrawerContent
                      cmsQuestDetail={cmsQuestDetail}
                      cmsQuestDetailUI={cmsQuestDetailUI}
                      onCloseDrawer={onCloseDrawer} // TODO - Remove this after replacing modal with RN Drawer/Page
                    />
                  )}
                </QuestProvider>
              )}
            </>
          )}
        </QuestDetailModal>
      )}
    </>
  );
};
